package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.Placeholders
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BACKGROUND_NOT_FIRST
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_ID
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_KEY
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_SLOT_REF
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.CANVAS_SIZE_MISMATCH
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.CAPTION_HAS_PLACEHOLDER
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.DUPLICATE_DEVICE_CLASS
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.DUPLICATE_ID
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.GROUP_TOO_DEEP
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.LITERAL_TEXT
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.OUT_OF_RANGE
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.SLOT_REPEATED
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.STRING_TOO_LONG
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.TOO_MANY_BOARDS
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.TOO_MANY_LAYERS
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.TOO_MANY_PAGES
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.UNSUPPORTED_VERSION
import dev.lingvo.shared.validateKeyName

/**
 * Every rule of SPEC-B 1.4 over a decoded document. Pure, never throws, and bounded: it does not descend past
 * the group-depth limit, so even a document built in memory with absurd nesting is walked in bounded depth.
 * Returns errors and warnings in document order; [DesignCodec.decode] refuses on any error.
 */
object DesignValidator {
    fun validate(design: ScreenshotDesign, devices: DeviceClassRule, fonts: FontNameRule = DesignFonts.V1): List<DesignIssue> {
        val sink = IssueSink(fonts)
        if (design.schemaVersion !in 1..DESIGN_SCHEMA_VERSION) {
            sink.add(UNSUPPORTED_VERSION, "/schemaVersion", "Design versions 1 through $DESIGN_SCHEMA_VERSION are read; found ${design.schemaVersion}.")
        }
        design.templateId?.let {
            if (!DesignLimits.TEMPLATE_ID_REGEX.matches(it.id)) sink.add(BAD_ID, "/templateId", "Not a template id: '${it.id.take(40)}'.")
        }
        brand(sink, design.brand)
        palette(sink, design.palette)
        sink.count("/boards", design.boards.size, 1, DesignLimits.MAX_BOARDS, TOO_MANY_BOARDS, "boards")
        val walk = DocumentWalk(design.schemaVersion)
        val boardIds = HashSet<String>()
        val deviceClasses = HashSet<String>()
        design.boards.forEachIndexed { index, board ->
            val path = "/boards/$index"
            if (!boardIds.add(board.id)) sink.add(DUPLICATE_ID, "$path/id", "Board id '${board.id.take(40)}' is used twice.")
            val canonical = deviceClass(sink, design.platform, board.deviceClass, "$path/deviceClass", devices)
            // Compared on the canonical id, so an alias or a case variant cannot add a second layout for a class.
            if (!deviceClasses.add(canonical ?: board.deviceClass.lowercase())) {
                sink.add(DUPLICATE_DEVICE_CLASS, "$path/deviceClass", "Device class '${board.deviceClass.take(64)}' has two layouts; each class has one board.")
            }
            board.derivation?.let { link ->
                val source = design.boards.firstOrNull { it.deviceClass == link.sourceDevice }
                if (design.schemaVersion < 2 || source == null || source === board || source.derivation != null) {
                    sink.add(BAD_ID, "$path/derivation", "A derived board requires schema 2 and an independent source board in this document.")
                }
            }
            board(sink, board, path, devices, canonical, walk)
        }
        if (walk.layers > DesignLimits.MAX_LAYERS_PER_DOCUMENT) {
            sink.add(TOO_MANY_LAYERS, "/boards", "At most ${DesignLimits.MAX_LAYERS_PER_DOCUMENT} layers per design; found ${walk.layers}.")
        }
        return sink.issues
    }

    /** State shared across the whole document: layer ids are unique per document, and layers are counted. */
    private class DocumentWalk(val schemaVersion: Int) {
        val layerIds = HashSet<String>()
        var layers = 0
    }

    private class PageContext(val slots: Int, val walk: DocumentWalk) {
        var layers = 0
    }

    private fun brand(sink: IssueSink, brand: BrandKit) {
        sink.text("/brand/name", brand.name, DesignLimits.MAX_BRAND_NAME)
        brand.primary?.let { sink.hex("/brand/primary", it, opaque = true) }
        brand.secondary?.let { sink.hex("/brand/secondary", it, opaque = true) }
        sink.font("/brand/displayFamily", brand.displayFamily)
        sink.font("/brand/textFamily", brand.textFamily)
        brand.logoAssetId?.let { sink.assetId("/brand/logoAssetId", it) }
        sink.bounded("/brand/iconPalette", brand.iconPalette, DesignLimits.MAX_ICON_PALETTE, "icon colours") { index, hex ->
            sink.hex("/brand/iconPalette/$index", hex, opaque = true)
        }
        sink.text("/brand/voice/notes", brand.voice.notes, DesignLimits.MAX_VOICE_NOTES)
        sink.bounded("/brand/voice/avoid", brand.voice.avoid, DesignLimits.MAX_AVOID_ITEMS, "words to avoid", STRING_TOO_LONG) { index, word ->
            sink.text("/brand/voice/avoid/$index", word, DesignLimits.MAX_AVOID_ITEM)
        }
    }

    private fun palette(sink: IssueSink, palette: Palette) {
        listOf(
            "bg" to palette.bg, "surface" to palette.surface, "ink" to palette.ink, "inkMuted" to palette.inkMuted,
            "accent" to palette.accent, "accentInk" to palette.accentInk,
        ).forEach { (name, value) -> sink.hex("/palette/$name", value, opaque = false) }
        sink.bounded("/palette/slotAccents", palette.slotAccents, DesignLimits.MAX_SLOT_ACCENTS, "slot accents") { index, hex ->
            sink.hex("/palette/slotAccents/$index", hex, opaque = false)
        }
    }

    /**
     * The canonical id of [raw] on [platform], or null (with an issue) when the class is unknown there. A known
     * class spelled any other way than its canonical id is refused, so a stored document has one spelling.
     */
    private fun deviceClass(sink: IssueSink, platform: StorePlatform, raw: String, path: String, devices: DeviceClassRule): String? {
        val canonical = try {
            devices.canonicalId(platform, raw)
        } catch (e: Exception) {
            null
        }
        when {
            canonical == null || !DesignLimits.DEVICE_CLASS_REGEX.matches(canonical) -> {
                sink.add(BAD_ID, path, "'${raw.take(64)}' is not a device class of ${platform.displayName}.")
                return null
            }
            canonical != raw -> sink.add(BAD_ID, path, "Write the device class as '$canonical'.")
        }
        return canonical
    }

    private fun board(sink: IssueSink, board: Board, path: String, devices: DeviceClassRule, deviceClass: String?, walk: DocumentWalk) {
        sink.id("$path/id", board.id)
        val limit = DesignLimits.COORD_LIMIT.toInt()
        val widthOk = sink.int("$path/canvas/width", board.canvas.width, 1, limit)
        val heightOk = sink.int("$path/canvas/height", board.canvas.height, 1, limit)
        if (deviceClass != null && widthOk && heightOk && !allows(devices, deviceClass, board.canvas)) {
            sink.add(
                CANVAS_SIZE_MISMATCH,
                "$path/canvas",
                "${board.canvas.width} x ${board.canvas.height} is not a store size for '${board.deviceClass.take(64)}'.",
            )
        }
        board.safeArea?.let { safeArea(sink, it, "$path/safeArea", board.canvas) }
        sink.count("$path/pages", board.pages.size, 1, DesignLimits.MAX_PAGES_PER_BOARD, TOO_MANY_PAGES, "pages")
        val totalSlots = board.pages.sumOf { it.slotIds.size }
        val maxSlots = minOf(DesignLimits.MAX_SLOTS_PER_BOARD, deviceClass?.let { slotsFor(devices, it) } ?: DesignLimits.MAX_SLOTS_PER_BOARD)
        if (totalSlots > maxSlots) {
            sink.add(TOO_MANY_PAGES, "$path/pages", "At most $maxSlots slots on this board; found $totalSlots.")
        }
        val pageIds = HashSet<String>()
        val slotIds = HashSet<String>()
        board.pages.forEachIndexed { index, page ->
            val pagePath = "$path/pages/$index"
            if (!pageIds.add(page.id)) sink.add(DUPLICATE_ID, "$pagePath/id", "Page id '${page.id.take(40)}' is used twice on this board.")
            page.slotIds.forEachIndexed { slotIndex, slotId ->
                sink.slotKey("$pagePath/slotIds/$slotIndex", slotId)
                if (!slotIds.add(slotId)) sink.add(SLOT_REPEATED, "$pagePath/slotIds/$slotIndex", "Slot '${slotId.take(40)}' appears twice on this board.")
            }
            page(sink, page, pagePath, walk)
            DesignWarnings.page(sink, board, page, pagePath)
        }
    }

    private fun allows(devices: DeviceClassRule, deviceClass: String, canvas: CanvasSize): Boolean = try {
        devices.allows(deviceClass, canvas.width, canvas.height)
    } catch (e: Exception) {
        false
    }

    private fun slotsFor(devices: DeviceClassRule, deviceClass: String): Int = try {
        devices.maxSlots(deviceClass)
    } catch (e: Exception) {
        DesignLimits.MAX_SLOTS_PER_BOARD
    }

    private fun safeArea(sink: IssueSink, insets: Insets, path: String, canvas: CanvasSize) {
        val ok = listOf(
            sink.num("$path/top", insets.top, 0.0, DesignLimits.COORD_LIMIT),
            sink.num("$path/right", insets.right, 0.0, DesignLimits.COORD_LIMIT),
            sink.num("$path/bottom", insets.bottom, 0.0, DesignLimits.COORD_LIMIT),
            sink.num("$path/left", insets.left, 0.0, DesignLimits.COORD_LIMIT),
        ).all { it }
        if (ok && (insets.left + insets.right >= canvas.width || insets.top + insets.bottom >= canvas.height)) {
            sink.add(OUT_OF_RANGE, path, "The safe area leaves no room on the canvas.")
        }
    }

    private fun page(sink: IssueSink, page: Page, path: String, walk: DocumentWalk) {
        sink.id("$path/id", page.id)
        if (page.slotIds.isEmpty()) sink.add(BAD_SLOT_REF, "$path/slotIds", "A page covers at least one slot.")
        if (page.slotIds.size > DesignLimits.MAX_SLOTS_PER_PAGE) {
            sink.add(TOO_MANY_PAGES, "$path/slotIds", "A panorama spans at most ${DesignLimits.MAX_SLOTS_PER_PAGE} slots; found ${page.slotIds.size}.")
        }
        scene(sink, page.scene, "$path/scene")
        val context = PageContext(page.slotIds.size, walk)
        page.layers.forEachIndexed { index, layer ->
            if (layer is BackgroundLayer && index != 0) {
                sink.add(BACKGROUND_NOT_FIRST, "$path/layers/$index", "A background must be the first layer, and a page has one.")
            }
            layer(sink, layer, "$path/layers/$index", context, groupDepth = 0)
        }
        if (context.layers > DesignLimits.MAX_LAYERS_PER_PAGE) {
            sink.add(TOO_MANY_LAYERS, "$path/layers", "At most ${DesignLimits.MAX_LAYERS_PER_PAGE} layers per page; found ${context.layers}.")
        }
        OverrideValidation.validate(sink, page, path, walk.schemaVersion)
    }

    private fun scene(sink: IssueSink, scene: SceneSpec, path: String) {
        sink.num("$path/captionShare", scene.captionShare, DesignLimits.MIN_CAPTION_SHARE, DesignLimits.MAX_CAPTION_SHARE)
        sink.num("$path/deviceScale", scene.deviceScale, DesignLimits.MIN_DEVICE_SCALE, DesignLimits.MAX_DEVICE_SCALE)
        sink.num("$path/tilt", scene.tilt, 0.0, DesignLimits.MAX_TILT)
    }

    private fun layer(sink: IssueSink, layer: Layer, path: String, context: PageContext, groupDepth: Int) {
        context.layers++
        context.walk.layers++
        val base = layer.base
        base(sink, base, "$path/base", context)
        if (!context.walk.layerIds.add(base.id)) {
            sink.add(DUPLICATE_ID, "$path/base/id", "Layer id '${base.id.take(40)}' is used twice in this design.")
        }
        when (layer) {
            is BackgroundLayer -> {
                if (groupDepth > 0) sink.add(BACKGROUND_NOT_FIRST, path, "A background cannot be inside a group.")
                sink.paint("$path/fill", layer.fill)
                sink.bounded("$path/blobs", layer.blobs, DesignLimits.MAX_BLOBS, "blobs") { index, blob ->
                    sink.num("$path/blobs/$index/cx", blob.cx, -1.0, 2.0)
                    sink.num("$path/blobs/$index/cy", blob.cy, -1.0, 2.0)
                    sink.num("$path/blobs/$index/r", blob.r, 0.0, 2.0)
                    sink.color("$path/blobs/$index/color", blob.color)
                }
                layer.grain?.let { sink.num("$path/grain/amount", it.amount, 0.0, DesignLimits.MAX_GRAIN) }
            }
            is DeviceLayer -> {
                sink.imageSource("$path/screen", layer.screen, context.slots)
                layer.screenRadius?.let { sink.num("$path/screenRadius", it, 0.0, 0.5) }
            }
            is ImageLayer -> {
                sink.imageSource("$path/source", layer.source, context.slots)
                layer.crop?.let { sink.crop("$path/crop", it) }
                sink.cornerRadius("$path/cornerRadius", layer.cornerRadius, base.box)
                layer.stroke?.let { sink.stroke("$path/stroke", it) }
            }
            is TextLayer -> {
                content(sink, layer.content, "$path/content", context)
                sink.style("$path/style", layer.style)
                sink.fit("$path/fit", layer.fit)
            }
            is ShapeLayer -> shape(sink, layer, path)
            is GroupLayer -> {
                sink.cornerRadius("$path/cornerRadius", layer.cornerRadius, base.box)
                if (groupDepth + 1 > DesignLimits.MAX_GROUP_DEPTH) {
                    sink.add(GROUP_TOO_DEEP, path, "Groups nest at most ${DesignLimits.MAX_GROUP_DEPTH} deep.")
                    return
                }
                layer.children.forEachIndexed { index, child ->
                    layer(sink, child, "$path/children/$index", context, groupDepth + 1)
                }
            }
        }
    }

    private fun base(sink: IssueSink, base: LayerBase, path: String, context: PageContext) {
        sink.id("$path/id", base.id)
        sink.text("$path/name", base.name, DesignLimits.MAX_LAYER_NAME)
        sink.box("$path/box", base.box)
        if (base.roleSlot < 0 || base.roleSlot >= maxOf(context.slots, 1)) {
            sink.add(BAD_SLOT_REF, "$path/roleSlot", "Role slot ${base.roleSlot} is not on this page.")
        }
        sink.num("$path/opacity", base.opacity, 0.0, 1.0)
        base.shadow?.let { sink.shadow("$path/shadow", it) }
    }

    private fun content(sink: IssueSink, content: TextContent, path: String, context: PageContext) {
        when (content) {
            is TextContent.SlotCaption -> sink.slot("$path/slot", content.slot, context.slots)
            is TextContent.Key -> validateKeyName(content.key)?.let { sink.add(BAD_KEY, "$path/key", it) }
            is TextContent.Literal -> literal(sink, content.text, "$path/text")
        }
    }

    private fun literal(sink: IssueSink, text: String, path: String) {
        sink.text(path, text, DesignLimits.MAX_LITERAL)
        if (text.isEmpty() || !text.all { it in '0'..'9' }) {
            sink.add(LITERAL_TEXT, path, "This text is the same in every language; use a caption key so translators can change it.")
        }
        if (Placeholders.extractAll(text).isNotEmpty()) {
            sink.add(CAPTION_HAS_PLACEHOLDER, path, "This text contains a placeholder, which a screenshot cannot fill.")
        }
    }

    private fun shape(sink: IssueSink, layer: ShapeLayer, path: String) {
        sink.cornerRadius("$path/cornerRadius", layer.cornerRadius, layer.base.box)
        layer.fill?.let { sink.paint("$path/fill", it) }
        layer.stroke?.let { sink.stroke("$path/stroke", it) }
        if (layer.shape == ShapeKind.CURVE) {
            if (layer.points.size < DesignLimits.MIN_CURVE_POINTS || layer.points.size > DesignLimits.MAX_CURVE_POINTS) {
                sink.add(OUT_OF_RANGE, "$path/points", "A curve needs ${DesignLimits.MIN_CURVE_POINTS} to ${DesignLimits.MAX_CURVE_POINTS} points; found ${layer.points.size}.")
                return
            }
        } else if (layer.points.isNotEmpty()) {
            sink.add(OUT_OF_RANGE, "$path/points", "Only a curve has points.")
            return
        }
        layer.points.forEachIndexed { index, point ->
            sink.num("$path/points/$index/x", point.x, -DesignLimits.COORD_LIMIT, DesignLimits.COORD_LIMIT)
            sink.num("$path/points/$index/y", point.y, -DesignLimits.COORD_LIMIT, DesignLimits.COORD_LIMIT)
        }
    }

    /** BCP-47 case: language lowercase, script title case, region uppercase, anything else lowercase. */
    internal fun canonicalLocale(tag: String): String = tag.split('-').mapIndexed { index, part ->
        when {
            index == 0 -> part.lowercase()
            part.length == 4 && part.all { it.isLetter() } -> part.lowercase().replaceFirstChar { it.uppercaseChar() }
            (part.length == 2 && part.all { it.isLetter() }) || (part.length == 3 && part.all { it in '0'..'9' }) -> part.uppercase()
            else -> part.lowercase()
        }
    }.joinToString("-")

}
